import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductContactModal from '../products/components/ProductContactModal';
import CaseStudySubmissionModal from './components/CaseStudySubmissionModal';

const CaseStudiesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);

  // Set document metadata using React 19 features
  useEffect(() => {
    document.title = 'Software Development Case Studies | PureCode Ltd Success Stories';
    
    // Update meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore PureCode Ltd\'s successful software development projects. Real client case studies showcasing custom solutions, results, and testimonials from healthcare, finance, e-commerce, and more industries.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'software development case studies, client success stories, custom software solutions, PureCode projects');
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Software Development Case Studies | PureCode Ltd Success Stories');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Explore PureCode Ltd\'s successful software development projects. Real client case studies showcasing custom solutions and results.');
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://purecode.uk/case-studies');
    }

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://purecode.uk/case-studies');

    // Add structured data
    let structuredData = document.querySelector('script[type="application/ld+json"]');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.setAttribute('type', 'application/ld+json');
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Case Studies - PureCode Ltd",
      "description": "Software development case studies and client success stories",
      "url": "https://purecode.uk/case-studies"
    });
  }, []);

  const caseStudies = [
    {
      id: 1,
      title: 'E-commerce Platform Transformation',
      client: 'RetailMax Solutions',
      industry: 'E-commerce',
      challenge: 'Legacy system causing slow performance and poor user experience',
      solution: 'Complete platform rebuild with modern architecture and cloud migration',
      results: '300% performance improvement, 45% increase in conversion rates',
      technologies: ['React', 'Node.js', 'AWS', 'MongoDB'],
      duration: '6 months',
      image: 'https://public.readdy.ai/ai/img_res/4a8a5ef41c44f6effa73d37c3f4d3137.jpg',
      testimonial: "PureCode transformed our outdated system into a high-performing platform that exceeded all our expectations."
    },
    {
      id: 2,
      title: 'Healthcare Management System',
      client: 'MedCare Group',
      industry: 'Healthcare',
      challenge: 'Manual processes causing inefficiencies and compliance issues',
      solution: 'Custom healthcare management system with automated workflows',
      results: '70% reduction in administrative time, 100% compliance achieved',
      technologies: ['Angular', 'C#', '.NET Core', 'Azure', 'SQL Server'],
      duration: '8 months',
      image: 'https://readdy.ai/api/search-image?query=healthcare%20management%20system%20interface%2C%20medical%20dashboard%2C%20patient%20records%2C%20clean%20modern%20design%2C%20professional%20healthcare%20technology%2C%20white%20background%20with%20blue%20accents&width=800&height=500&seq=cs2&orientation=landscape',
      testimonial: "The system streamlined our operations and significantly improved patient care delivery."
    },
    {
      id: 3,
      title: 'Financial Analytics Platform',
      client: 'InvestPro Capital',
      industry: 'Finance',
      challenge: 'Need for real-time financial data analysis and reporting',
      solution: 'Advanced analytics platform with AI-powered insights',
      results: '250% faster decision making, £2M cost savings in first year',
      technologies: ['Python', 'Django', 'TensorFlow', 'PostgreSQL', 'Docker'],
      duration: '10 months',
      image: 'https://readdy.ai/api/search-image?query=financial%20analytics%20dashboard%2C%20charts%20and%20graphs%2C%20data%20visualization%2C%20professional%20trading%20platform%20interface%2C%20modern%20fintech%20design%2C%20white%20background%20with%20green%20accents&width=800&height=500&seq=cs3&orientation=landscape',
      testimonial: "The platform gave us the competitive edge we needed with superior market insights."
    },
    {
      id: 4,
      title: 'Manufacturing Automation System',
      client: 'TechManufacturing Ltd',
      industry: 'Manufacturing',
      challenge: 'Manual quality control and production tracking inefficiencies',
      solution: 'IoT-based automation system with real-time monitoring',
      results: '40% reduction in defects, 60% improvement in production efficiency',
      technologies: ['IoT Sensors', 'C#', 'Azure IoT', 'Power BI', 'SQL Server'],
      duration: '12 months',
      image: 'https://readdy.ai/api/search-image?query=manufacturing%20automation%20control%20system%2C%20industrial%20IoT%20dashboard%2C%20production%20monitoring%20interface%2C%20modern%20factory%20technology%2C%20clean%20technical%20design%2C%20white%20background%20with%20orange%20accents&width=800&height=500&seq=cs4&orientation=landscape',
      testimonial: "PureCode's automation solution revolutionized our production line efficiency."
    },
    {
      id: 5,
      title: 'Educational Learning Platform',
      client: 'EduTech Academy',
      industry: 'Education',
      challenge: 'Need for engaging online learning platform during remote education shift',
      solution: 'Interactive e-learning platform with video streaming and assessments',
      results: '85% student engagement increase, 95% course completion rate',
      technologies: ['Vue.js', 'Laravel', 'MySQL', 'AWS S3', 'WebRTC'],
      duration: '5 months',
      image: 'https://readdy.ai/api/search-image?query=educational%20learning%20platform%20interface%2C%20online%20classroom%2C%20video%20streaming%2C%20student%20dashboard%2C%20modern%20e-learning%20design%2C%20bright%20educational%20theme%2C%20white%20background%20with%20purple%20accents&width=800&height=500&seq=cs5&orientation=landscape',
      testimonial: "The platform transformed how we deliver education and engage with students."
    },
    {
      id: 6,
      title: 'Logistics Optimization System',
      client: 'GlobalShip Logistics',
      industry: 'Logistics',
      challenge: 'Complex supply chain management and route optimization needs',
      solution: 'AI-powered logistics management system with predictive analytics',
      results: '30% reduction in delivery times, 25% cost savings on fuel',
      technologies: ['React Native', 'Python', 'Machine Learning', 'Google Maps API', 'Firebase'],
      duration: '9 months',
      image: 'https://readdy.ai/api/search-image?query=logistics%20management%20system%2C%20route%20optimization%20dashboard%2C%20shipping%20tracking%20interface%2C%20supply%20chain%20visualization%2C%20professional%20logistics%20technology%2C%20white%20background%20with%20red%20accents&width=800&height=500&seq=cs6&orientation=landscape',
      testimonial: "The optimization system gave us unprecedented visibility and control over our operations."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=successful%20software%20development%20projects%20showcase%2C%20modern%20tech%20achievements%2C%20professional%20business%20success%20story%2C%20clean%20corporate%20background%2C%20inspiring%20technology%20solutions%2C%20white%20and%20teal%20color%20scheme&width=1920&height=800&seq=cshero&orientation=landscape"
            alt="Case Studies Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Client Success Stories
          </h1>
          <p className="text-xl md:text-2xl text-gray-100 mb-8 max-w-3xl mx-auto">
            Discover how we've helped businesses transform their operations with innovative software solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-teal-500 hover:to-cyan-500 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Start Your Project
            </button>
            <a
              href="/contact"
              className="px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 cursor-pointer whitespace-nowrap"
            >
              Get Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real projects, real results. See how we've helped businesses across various industries achieve their goals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {caseStudies.map((study) => (
              <article key={study.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative h-64">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-teal-600 text-white text-sm font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{study.client}</p>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Challenge:</h4>
                      <p className="text-gray-600 text-sm">{study.challenge}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Solution:</h4>
                      <p className="text-gray-600 text-sm">{study.solution}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Results:</h4>
                      <p className="text-teal-600 text-sm font-medium">{study.results}</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {study.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span>Duration: {study.duration}</span>
                    </div>
                    <blockquote className="text-gray-600 italic">
                      "{study.testimonial}"
                    </blockquote>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Client Submission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-text-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Share Your Success Story
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Are you a PureCode client with a success story to share? We'd love to feature your project and showcase how we helped transform your business. Your story could inspire others!
            </p>
            <button
              onClick={() => setIsSubmissionModalOpen(true)}
              className="px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-teal-500 hover:to-cyan-500 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap inline-flex items-center"
            >
              <i className="ri-add-circle-line text-xl mr-2"></i>
              Submit Your Case Study
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Create Your Success Story?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            Let's discuss how we can help transform your business with custom software solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Start Your Project
            </button>
            <a
              href="/contact"
              className="px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 cursor-pointer whitespace-nowrap"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <ProductContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productName="Custom Software Development"
        actionType="Start Project"
      />

      <CaseStudySubmissionModal
        isOpen={isSubmissionModalOpen}
        onClose={() => setIsSubmissionModalOpen(false)}
      />
    </div>
  );
};

export default CaseStudiesPage;
